import React, { useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Button } from 'reactstrap';
import { useDispatch } from 'react-redux';

import icons from '../../assets/icons';
import API from '../../services/api';
import siteUtils from '../../utils/site';
import { setAlert } from '../../redux/dashboard/actions';
import utils from '../../utils';
import CustomConfirm from '../customConfirm';
import Dropdown from '../dropdownSearch/dropdown';
import SearchBox from '../searchBox';
import Tags from '../tags';
import './styles.scss';

const getSiteId = attachedSite => {
  if (!attachedSite) return '';
  if (typeof attachedSite === 'object') return attachedSite.id || '';
  return attachedSite.split('/').pop();
};

const AssetDeviceSelector = ({
  assetId,
  disabled,
  onChange,
  preload = false,
  projectId,
  selectedDevices = [],
  siteId,
  site,
  onSiteUpdated,
  tagsClassName = ''
}) => {
  const [choices, setChoices] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingMove, setPendingMove] = useState(null);
  const [pendingDatasourceUpgrade, setPendingDatasourceUpgrade] = useState(null);
  const requestId = useRef(0);
  const selectorRef = useRef(null);
  const dispatch = useDispatch();

  const loadGenericChoices = async search => {
    const devices = [];
    const visitedPageTokens = new Set();
    const filter = search
      ? encodeURI(`displayName ILIKE "%${search}%" OR id ILIKE "%${search}%"`)
      : '';
    let pageToken = '';

    do {
      if (visitedPageTokens.has(pageToken)) throw new Error('The Devices API returned a repeated page token.');
      visitedPageTokens.add(pageToken);
      const response = await API.getDevices(projectId, pageToken, filter, 200);
      devices.push(...(response.devices || []));
      pageToken = response.nextPageToken || '';
    } while (pageToken);

    return devices.map(device => {
      const attachedSites = device.attachedSites || [];
      const currentSite = attachedSites.find(attachedSite => getSiteId(attachedSite) === siteId);
      const conflictingSite = attachedSites.find(attachedSite => getSiteId(attachedSite) !== siteId);
      const attachedSite = conflictingSite || currentSite;

      return {
        attachable: attachedSites.length === 0 || Boolean(currentSite && !conflictingSite),
        attachedSite: attachedSite
          ? {
              displayName: typeof attachedSite === 'object' ? attachedSite.displayName : getSiteId(attachedSite),
              id: getSiteId(attachedSite)
            }
          : null,
        displayName: device.displayName || device.id,
        id: device.id,
        solution: device.solution
      };
    });
  };

  const loadChoices = async (search = '') => {
    if (!projectId) return;

    const currentRequestId = requestId.current + 1;
    requestId.current = currentRequestId;
    setLoading(true);
    try {
      const response = assetId
        ? await API.getAllAssetDeviceChoices(projectId, siteId, assetId, [], search)
        : { deviceChoices: await loadGenericChoices(search) };
      if (requestId.current !== currentRequestId) return;
      setChoices(response.deviceChoices || []);
    } catch (error) {
      console.error(error);
      dispatch(setAlert(utils.generateAlert('Could not load Asset Device choices.', 'error')));
    } finally {
      if (requestId.current === currentRequestId) setLoading(false);
    }
  };

  useEffect(() => {
    if (assetId && preload) loadChoices();
  }, [assetId, preload, projectId, siteId]);

  useEffect(() => {
    if (choices.length === 0) return;
    let changed = false;
    const enrichedDevices = selectedDevices.map(device => {
      const choice = choices.find(item => item.id === device.id);
      if (!choice || device.displayName === choice.displayName) return device;
      changed = true;
      return { ...device, displayName: choice.displayName };
    });
    if (changed) onChange(enrichedDevices);
  }, [choices]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnOutsideClick = event => {
      if (!selectorRef.current?.contains(event.target)) setIsOpen(false);
    };
    const closeOnEscape = event => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  const searchChoices = useDebouncedCallback(search => loadChoices(search), 300);

  const addDevice = choice => {
    if (selectedDevices.some(device => device.id === choice.id)) return;
    onChange([
      ...selectedDevices,
      {
        ...choice,
        id: choice.id,
        displayName: choice.displayName || choice.id,
        requiresDetach: !choice.attachable
      }
    ]);
    setIsOpen(false);
  };

  const confirmMove = () => {
    if (!pendingMove) return;
    requestAddDevice(pendingMove);
    setPendingMove(null);
  };

  const requestAddDevice = async choice => {
    if (!site) {
      addDevice(choice);
      return;
    }

    try {
      const device = choice.solution ? choice : await API.getDevice(projectId, choice.id);
      const datasource = siteUtils.getDatasourceForDevice(device);
      const currentDatasources = site.datasources || [];

      if (!datasource || currentDatasources.includes(datasource)) {
        addDevice({ ...choice, solution: device.solution });
        return;
      }

      setIsOpen(false);
      setPendingDatasourceUpgrade({
        choice: { ...choice, solution: device.solution },
        datasources: [...currentDatasources, datasource]
      });
    } catch (error) {
      console.error(error);
      dispatch(setAlert(utils.generateAlert('Could not verify the Device data source.', 'error')));
    }
  };

  const confirmDatasourceUpgrade = async () => {
    if (!pendingDatasourceUpgrade) return;

    try {
      if (site?.id && site?.projectId) {
        await API.updateSite({ ...site, datasources: pendingDatasourceUpgrade.datasources }, ['datasources']);
      }
      onSiteUpdated?.(currentSite => ({
        ...currentSite,
        datasources: pendingDatasourceUpgrade.datasources
      }));
      addDevice(pendingDatasourceUpgrade.choice);
      setPendingDatasourceUpgrade(null);
    } catch (error) {
      console.error(error);
      dispatch(setAlert(utils.generateAlert('Could not upgrade this Site data source.', 'error')));
    }
  };

  const availableChoices = choices.filter(
    choice => choice.attachable && !selectedDevices.some(device => device.id === choice.id)
  );
  const unavailableChoices = choices.filter(
    choice => !choice.attachable && !selectedDevices.some(device => device.id === choice.id)
  );

  return (
    <div className="asset-device-selector" ref={selectorRef}>
      <div className="dropdown-search outline left-align">
        <Dropdown
          customWidth={520}
          hasBlanket={false}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          target={
            <Button
              active={isOpen}
              disabled={disabled}
              onClick={() => {
                const nextOpen = !isOpen;
                setIsOpen(nextOpen);
                if (nextOpen) loadChoices();
              }}
            >
              <span className="current-value">Select Device</span>
              <img src={icons.angleDown} className="arrow-down" alt="arrow-down" />
            </Button>
          }
        >
          <SearchBox onChange={searchChoices} placeholder="Search..." className="outline" autoFocus />
          <div className="asset-device-choice-list">
            {loading ? (
              <div className="asset-device-choice-message">Loading...</div>
            ) : (
              <>
                <div className="asset-device-choice-group-title">Available Devices</div>
                {availableChoices.length > 0 ? (
                  availableChoices.map(choice => (
                    <button
                      type="button"
                      className="asset-device-choice available"
                      key={choice.id}
                      onClick={() => requestAddDevice(choice)}
                    >
                      <span>{choice.displayName || choice.id}</span>
                      <i className="uil uil-plus" />
                    </button>
                  ))
                ) : (
                  <div className="asset-device-choice-message">No available Devices</div>
                )}

                <div className="asset-device-choice-group-title unavailable">Attached to another Site</div>
                {unavailableChoices.length > 0 ? (
                  unavailableChoices.map(choice => {
                    const attachedSite = choice.attachedSite;
                    const assetNames = (attachedSite?.assets || [])
                      .map(asset => asset.displayName || asset.id)
                      .join(', ');
                    return (
                      <div className="asset-device-choice unavailable" key={choice.id}>
                        <div className="asset-device-choice-details">
                          <span className="asset-device-choice-name">
                            <strong>Device:</strong> {choice.displayName || choice.id}
                          </span>
                          <span className="asset-device-choice-attachment">
                            <span>
                              <strong>Site:</strong>{' '}
                              {attachedSite?.displayName || attachedSite?.id || 'Another Site'}
                            </span>
                            <span>
                              <strong>Asset:</strong> {assetNames || 'Unknown Asset'}
                            </span>
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-link"
                          onClick={() => {
                            setIsOpen(false);
                            setPendingMove(choice);
                          }}
                        >
                          Move here
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <div className="asset-device-choice-message">No unavailable Devices</div>
                )}
              </>
            )}
          </div>
        </Dropdown>
      </div>

      <div className={`asset-device-tags ${tagsClassName}`}>
        {selectedDevices.map(device => (
          <Tags
            key={device.id}
            value={device.displayName || device.id}
            hasCloseButton
            onClose={() => onChange(selectedDevices.filter(item => item.id !== device.id))}
          />
        ))}
      </div>

      <CustomConfirm
        messageType="moveAssetDevice"
        messageContext={pendingMove}
        isOpen={Boolean(pendingMove)}
        cancelCb={() => setPendingMove(null)}
        confirmCb={confirmMove}
      />
      <CustomConfirm
        messageType="upgradeSiteDatasources"
        messageContext={
          pendingDatasourceUpgrade && {
            site,
            datasources: pendingDatasourceUpgrade.datasources
          }
        }
        isOpen={Boolean(pendingDatasourceUpgrade)}
        cancelCb={() => setPendingDatasourceUpgrade(null)}
        confirmCb={confirmDatasourceUpgrade}
      />
    </div>
  );
};

export default AssetDeviceSelector;
